import React, { useState, useRef, useEffect, memo } from 'react';
import TinderCard from 'react-tinder-card';
import { Event } from '@/types/event';
import { 
  IconHeart, 
  IconX, 
  IconBookmark, 
  IconCalendarEvent, 
  IconMapPin,
  IconInfoCircle
} from '@tabler/icons-react';
import styles from './SwipeCard.module.css';

interface SwipeCardProps {
  event: Event;
  onSwipe: (direction: string) => void;
  onInfoClick: () => void;
  zIndex: number;
  isTop: boolean;
  className?: string;
}

const SwipeCard: React.FC<SwipeCardProps> = ({ 
  event, 
  onSwipe, 
  onInfoClick,
  zIndex,
  isTop,
  className = ''
}) => {
  const [swipeDirection, setSwipeDirection] = useState<string | null>(null);
  const cardRef = useRef<any>(null);
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= 768);
  const [hasTriggeredAction, setHasTriggeredAction] = useState<boolean>(false);

  // Detect mobile on mount and resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Format date to display in a user-friendly format
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    
    // Format: "18 Avr, 19:00"
    const options: Intl.DateTimeFormatOptions = { 
      day: 'numeric', 
      month: 'short',
      hour: '2-digit', 
      minute: '2-digit'
    };
    
    return date.toLocaleDateString('fr-FR', options).replace(',', '');
  };
  
  // Generate the appropriate style for card background
  const getCardStyle = () => {
    const defaultImage = 'https://images.unsplash.com/photo-1501281668745-f7f57925c3b4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1050&q=80';
    
    return {
      backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.7)), url(${event.imageUrl || defaultImage})`,
    };
  };

  // Trigger swipe programmatically when button is clicked
  const handleButtonSwipe = (direction: string) => {
    if (hasTriggeredAction) return; // Éviter les actions multiples
    
    setHasTriggeredAction(true);
    
    if (cardRef.current) {
      // Use the TinderCard's swipe method
      cardRef.current.swipe(direction);
      // Ne pas appeler onSwipe ici, il sera appelé par handleSwipeComplete
    } else {
      // Fallback si ref n'est pas disponible
      onSwipe(direction);
      
      // Reset le flag après un délai pour permettre l'animation
      // Utiliser requestAnimationFrame pour synchroniser avec le prochain frame de rendu
      requestAnimationFrame(() => {
        setTimeout(() => {
          setHasTriggeredAction(false);
        }, 100); // Réduction du délai pour améliorer la réactivité
      });
    }
  };

  // Custom handler for the Tinder Card's onCardLeftScreen/onSwipe events
  const handleSwipeComplete = (direction: string) => {
    if (hasTriggeredAction) return; // Éviter les actions multiples
    
    setHasTriggeredAction(true);
    setSwipeDirection(direction);
    
    // Small delay before calling the parent's onSwipe handler
    // to allow animation to complete - mais réduit pour meilleure réactivité
    requestAnimationFrame(() => {
      setTimeout(() => {
        onSwipe(direction);
        setHasTriggeredAction(false);
      }, 100); // Réduction du délai de 300ms à 100ms
    });
  };

  // Handler for swiping motion (used by TinderCard)
  const handleSwipeUpdate = (direction: string | null) => {
    if (direction !== swipeDirection) {
      setSwipeDirection(direction);
    }
  };

  // Combiner les classes CSS pour inclure les classes d'animation
  const cardContainerClasses = `${styles.cardContainer} ${className}`;

  return (
    <div className={cardContainerClasses} style={{ zIndex }}>
      <TinderCard
        ref={cardRef}
        className={styles.tinderCard}
        onSwipe={handleSwipeComplete}
        onCardLeftScreen={() => {}}
        preventSwipe={[]} // Allow all directions
        swipeRequirementType={isMobile ? "velocity" : "position"}
        swipeThreshold={isMobile ? 25 : 50}
        flickOnSwipe={true}
        onSwipeRequirementFulfilled={(dir) => handleSwipeUpdate(dir)}
        onSwipeRequirementUnfulfilled={() => handleSwipeUpdate(null)}
      >
        <div 
          className={styles.card} 
          style={getCardStyle()}
        >
          {/* Swipe indicators */}
          {isTop && (
            <>
              <div className={`${styles.swipeIndicator} ${styles.leftIndicator} ${swipeDirection === 'left' ? styles.active : ''}`}>
                <IconX size={36} stroke={3} />
              </div>
              <div className={`${styles.swipeIndicator} ${styles.rightIndicator} ${swipeDirection === 'right' ? styles.active : ''}`}>
                <IconHeart size={36} stroke={3} />
              </div>
              <div className={`${styles.swipeIndicator} ${styles.upIndicator} ${swipeDirection === 'up' ? styles.active : ''}`}>
                <IconBookmark size={36} stroke={3} />
              </div>
            </>
          )}

          {/* Info button */}
          <button 
            className={styles.infoButton}
            onClick={(e) => {
              e.stopPropagation();
              onInfoClick();
            }}
          >
            <IconInfoCircle size={24} />
          </button>

          <div className={styles.cardContent}>
            {/* Category badge */}
            <div 
              className={styles.categoryBadge}
              style={{ 
                backgroundColor: event.eventCategory?.color || '#666',
                boxShadow: `0 2px 8px ${event.eventCategory?.color || '#666'}80`
              }}
            >
              {event.eventCategory?.name || 'Événement'}
            </div>
            
            {/* Event details */}
            <div className={styles.eventDetails}>
              <h2 className={styles.eventTitle}>{event.title}</h2>
              
              <div className={styles.eventMeta}>
                <div className={styles.metaItem}>
                  <IconCalendarEvent size={16} />
                  <span>{formatDate(event.dateTime)}</span>
                </div>
                <div className={styles.metaItem}>
                  <IconMapPin size={16} />
                  <span className={styles.location}>{event.location}</span>
                </div>
              </div>
              
              {event.organization && (
                <div className={styles.organizer}>
                  {event.organization.name}
                </div>
              )}
            </div>
          </div>
        </div>
      </TinderCard>

      {/* Action buttons */}
      {isTop && (
        <div className={styles.actionButtons}>
          <button 
            className={`${styles.actionButton} ${styles.dislikeButton}`} 
            onClick={() => handleButtonSwipe('left')}
            aria-label="Passer"
            title="Passer"
          >
            <IconX size={28} stroke={2.5} />
          </button>
          
          <button 
            className={`${styles.actionButton} ${styles.saveButton}`} 
            onClick={() => handleButtonSwipe('up')}
            aria-label="Sauvegarder"
            title="Sauvegarder"
          >
            <IconBookmark size={28} stroke={2.5} />
          </button>
          
          <button 
            className={`${styles.actionButton} ${styles.likeButton}`} 
            onClick={() => handleButtonSwipe('right')}
            aria-label="J'aime"
            title="J'aime"
          >
            <IconHeart size={28} stroke={2.5} />
          </button>
        </div>
      )}
    </div>
  );
};

// Utilisation de React.memo pour éviter les rendus inutiles quand les props ne changent pas
// On définit un comparateur personnalisé qui vérifie uniquement les props essentielles
const arePropsEqual = (prevProps: SwipeCardProps, nextProps: SwipeCardProps) => {
  return (
    prevProps.event.id === nextProps.event.id &&
    prevProps.zIndex === nextProps.zIndex &&
    prevProps.isTop === nextProps.isTop &&
    prevProps.className === nextProps.className
  );
};

export default memo(SwipeCard, arePropsEqual);
